// Documents.tsx
import React, { useEffect, useState, ChangeEvent } from 'react';
import { Button, ConfirmationModal, PageShell, Table } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch, fetchOtherDocuments } from 'data';
import { addUserDocument, removeUserDocument } from 'data';
import { useTranslation } from 'react-i18next';

interface Document {
  id: number;
  filename: string;
  key: string;
  extension: string;
  url: string;
}

export const Documents = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { otherDocuments, loading, error } = useSelector(
    (state: RootState) => state.document,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [selectedDocType] = useState('default');

  useEffect(() => {
    dispatch(fetchOtherDocuments());
  }, [dispatch]);

  const handleUploadDocument = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        await dispatch(
          addUserDocument({
            file,
            docType: selectedDocType,
          }),
        ).unwrap();
        console.log(t('documents.toast.documentUploadedSuccessfully'));
        event.target.value = '';
      } catch (e) {
        console.log(t('documents.toast.failedToUploadDocument'), e);
      }
    } else {
      console.log(t('documents.toast.noTokenAvailable'));
    }
  };

  const handleDeleteDocument = async () => {
    if (selectedDocumentId) {
      try {
        await dispatch(
          removeUserDocument({
            id: selectedDocumentId,
          }),
        ).unwrap();
        setIsModalOpen(false);
        console.log(t('documents.toast.documentDeletedSuccessfully'));
      } catch (e) {
        console.log(t('documents.toast.failedToDeleteDocument'), e);
      }
    } else {
      console.log(t('documents.toast.noTokenAvailable'));
    }
  };

  const openDeleteModal = (documentId: number) => {
    setSelectedDocumentId(documentId);
    setIsModalOpen(true);
  };

  const downloadDocument = (doc: Document) => {
    if (!doc.url) {
      console.error(t('documents.toast.noDocumentDataAvailable'));
      return;
    }
    const link = document.createElement('a');
    link.href = doc.url;
    link.download = doc.filename || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActions = (row: Record<string, any>) => {
    const doc = row as Document; // Type assertion
    return [
      {
        label: t('documents.download'),
        onClick: () => downloadDocument(doc),
      },
      {
        label: t('documents.delete'),
        onClick: () => openDeleteModal(doc.id),
        className: 'text-red-600 hover:bg-red-100',
      },
    ];
  };

  return (
    <>
      <PageShell
        title={t('documents.documents')}
        actions={
          <>
            <input
              type="file"
              id="document-upload"
              className="hidden"
              onChange={handleUploadDocument}
              disabled={loading}
            />
            <Button
              label={t('documents.uploadDocument')}
              onClick={() =>
                document.getElementById('document-upload')?.click()
              }
              disabled={loading}
              loading={loading}
              fullWidth
            />
          </>
        }
      >

        {loading && (
          <p className="text-center text-gray-500">
            {t('documents.loadingDocuments')}
          </p>
        )}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && otherDocuments.length > 0 ? (
          <Table
            data={otherDocuments.map((doc) => ({
				id: doc.id,
				filename: doc.filename,
				key: doc.key,
				extension: doc.extension,
				url: doc.url,
			}))}
            columns={[{ key: 'filename', label: t('documents.documentName') }]}
            searchField={true}
            searchFunctionality={true}
            actions={getActions}
            cardLayout={{ title: 'filename' }}
          />
        ) : (
          !loading && (
            <p className="text-center text-gray-500">
              {t('documents.noDocumentsFound')}
            </p>
          )
        )}
      </PageShell>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteDocument}
        title={t('documents.deleteDocument')}
        message={t('documents.areYouSureYouWantToDeleteThisDocument')}
        loading={loading}
      />
    </>
  );
};
